import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Heart, Users, MessageSquare, Target } from 'lucide-react';
import { DealerSurveyData } from '@/api/types';
import { useTranslation } from 'react-i18next';

interface FormStepTwoProps {
  formData: DealerSurveyData;
  updateFormData: (formData: Partial<DealerSurveyData>) => void;
  readOnly?: boolean;
}

const FormStepTwo: React.FC<FormStepTwoProps> = ({ formData, updateFormData, readOnly = false }) => {
  const { t } = useTranslation('common');

  const handleRadioChange = (field: keyof DealerSurveyData, value: string) => {
    updateFormData({ [field]: value });
  };

  const handleCheckboxChange = (field: keyof DealerSurveyData, value: string, checked: boolean) => {
    const currentArray = formData[field] as string[] || [];
    let newArray: string[];
    
    if (checked) {
      newArray = [...currentArray, value];
    } else {
      newArray = currentArray.filter(item => item !== value);
    }
    
    updateFormData({ [field]: newArray });
  };

  const handleTextChange = (field: keyof DealerSurveyData, value: string) => {
    updateFormData({ [field]: value });
  };

  return (
    <div className={`space-y-8 ${readOnly ? 'pointer-events-none opacity-75' : ''}`}>
      {/* První dojem z designu */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Heart className="text-red-500" size={20} />
            {t('form_step_3.design_impression_title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={formData.designImpression}
            onValueChange={(value) => handleRadioChange('designImpression', value)}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            {[
              { value: 'very-positive', label: t('form_step_3.design_very_positive'), color: 'text-green-600' },
              { value: 'rather-positive', label: t('form_step_3.design_rather_positive'), color: 'text-green-500' },
              { value: 'neutral', label: t('form_step_3.design_neutral'), color: 'text-yellow-500' },
              { value: 'rather-negative', label: t('form_step_3.design_rather_negative'), color: 'text-orange-500' },
              { value: 'negative', label: t('form_step_3.design_negative'), color: 'text-red-500' }
            ].map((option) => (
              <div key={option.value} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <RadioGroupItem value={option.value} id={option.value} />
                <Label htmlFor={option.value} className={`text-slate-700 cursor-pointer ${option.color}`}>
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Zájem o spolupráci */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="text-orange-500" size={20} />
            {t('form_step_3.cooperation_interest_title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={formData.cooperationInterest}
            onValueChange={(value) => handleRadioChange('cooperationInterest', value)}
            className="grid grid-cols-1 lg:grid-cols-3 gap-4"
          >
            {[
              { value: 'yes', label: t('form_step_3.cooperation_yes'), color: 'text-green-600' },
              { value: 'maybe', label: t('form_step_3.cooperation_maybe'), color: 'text-yellow-600' },
              { value: 'no', label: t('form_step_3.cooperation_no'), color: 'text-red-500' }
            ].map((option) => (
              <div key={option.value} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <RadioGroupItem value={option.value} id={`coop-${option.value}`} />
                <Label htmlFor={`coop-${option.value}`} className={`text-slate-700 cursor-pointer ${option.color}`}>
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Reakce na značku */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="text-purple-500" size={20} />
            {t('form_step_3.brand_reaction_title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={formData.brandReaction}
            onValueChange={(value) => handleRadioChange('brandReaction', value)}
            className="space-y-3"
          >
            {[
              { value: 'knows-positive', label: t('form_step_3.brand_knows_positive') },
              { value: 'knows-negative', label: t('form_step_3.brand_knows_negative') },
              { value: 'unknown-interested', label: t('form_step_3.brand_unknown_interested') },
              { value: 'unknown-notrust', label: t('form_step_3.brand_unknown_notrust') }
            ].map((option) => (
              <div key={option.value} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <RadioGroupItem value={option.value} id={`brand-${option.value}`} />
                <Label htmlFor={`brand-${option.value}`} className="text-slate-700 cursor-pointer">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Má zájem o */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Target className="text-indigo-500" size={20} />
            {t('form_step_3.interests_title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { value: 'training', label: t('form_step_3.interest_training') },
              { value: 'presentation', label: t('form_step_3.interest_presentation') },
              { value: 'exclusive-region', label: t('form_step_3.interest_exclusive') },
              { value: 'marketing-materials', label: t('form_step_3.interest_materials') }
            ].map((option) => (
              <div key={option.value} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <Checkbox
                  id={`interest-${option.value}`}
                  checked={formData.interests?.includes(option.value) || false}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange('interests', option.value, checked as boolean)
                  }
                />
                <Label htmlFor={`interest-${option.value}`} className="text-slate-700 cursor-pointer">
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-base">{t('form_step_3.next_steps_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { value: 'send-materials', label: t('form_step_3.step_send_materials') },
              { value: 'price-offer', label: t('form_step_3.step_price_offer') },
              { value: 'send-sample', label: t('form_step_3.step_send_sample') },
              { value: 'next-visit', label: t('form_step_3.step_next_visit') }
            ].map((option) => (
              <div key={option.value} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <Checkbox
                  id={`step-${option.value}`}
                  checked={formData.nextSteps?.includes(option.value) || false}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange('nextSteps', option.value, checked as boolean)
                  }
                />
                <Label htmlFor={`step-${option.value}`} className="text-slate-700 cursor-pointer">
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="otherStep" className="text-slate-700 font-medium">
              {t('form_step_3.other_step')}
            </Label>
            <Input
              id="otherStep"
              value={formData.otherStep || ''}
              onChange={(e) => handleTextChange('otherStep', e.target.value)}
              placeholder={t('form_step_3.other_step_placeholder')}
              className="h-12 border-slate-300 focus:border-amber-500"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="comments" className="text-slate-700 font-medium">
              {t('form_step_3.comments')}
            </Label>
            <Textarea
              id="comments"
              value={formData.comments || ''}
              onChange={(e) => handleTextChange('comments', e.target.value)}
              placeholder={t('form_step_3.comments_placeholder')}
              className="min-h-[120px] border-slate-300 focus:border-amber-500"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FormStepTwo; 